"use client";

import { useState, useCallback, useEffect } from "react";
import { useIsMobile } from "../hooks/use-mobile";
import MainLayout from "../components/MainLayout";
import ProfileCard from "../components/chat_components/ProfileCard";
import FilterSidebar from "../components/chat_components/FilterSidebar";
import MobileFilterModal from "../components/chat_components/MobileFilterModal";
import { useChatFilters } from "../hooks/useChatFilters";
import chatService from "../services/chat";
import { Loader2 } from "lucide-react";

const SayHello = () => {
  const isMobile = useIsMobile();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    filters,
    updateFilters,
    resetFilters,
    getApiFilters,
    applyFilters,
    isLoading
  } = useChatFilters();

  useEffect(() => {
    async function fetchProfiles() {
      setIsProcessing(true);
      try {
        const profiles = await chatService.fetchProfiles();
        setProfiles(profiles.profiles);
      } catch (error) {
        console.error("Error while fetching profiles: ", error);
      } finally {
        setIsProcessing(false);
      }
    }
    fetchProfiles();
  }, []);

  // API call function for fetching filtered profiles
  const fetchFilteredProfiles = useCallback(async apiFilters => {
    try {
      const data = await chatService.fetchProfiles(apiFilters);
      setProfiles(data.profiles);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    }
  }, []);

  const handleApplyFilters = useCallback(
    () => {
      applyFilters(fetchFilteredProfiles);
    },
    [applyFilters, fetchFilteredProfiles]
  );

  const handleFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  // Desktop Right Sidebar Component
  const rightSidebar = !isMobile ? (
    <FilterSidebar
      filters={filters}
      onResetFilters={() => {
        resetFilters();
        applyFilters(fetchFilteredProfiles);
      }}
      onFiltersChange={updateFilters}
      onApplyFilters={handleApplyFilters}
    />
  ) : null;

  // Main content component
  const mainContent = (
    <div className="min-h-screen bg-white">
      {isProcessing && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <Loader2 className="animate-spin" width={64} height={64} />
        </div>
      )}

      {/* Mobile Header - only shown on mobile outside MainLayout */}
      {isMobile && (
        <div className="px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Say Hello!</h1>
            <div className="flex items-center gap-4">
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <img
                  src="/chat/icons/search.png"
                  alt="Search"
                  className="w-6 h-6"
                />
              </button>
              <button
                className={`p-2 rounded-lg transition-colors ${
                  isFilterOpen ? "bg-purple-100" : "hover:bg-gray-100"
                }`}
                onClick={handleFilterToggle}
              >
                <img
                  src="/chat/icons/filter.png"
                  alt="Filter"
                  className="w-6 h-6"
                />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <img
                  src="/chat/icons/notification.png"
                  alt="Notifications"
                  className="w-6 h-6"
                />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Header - shown inside MainLayout */}
      {!isMobile && (
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Say Hello!
              </h1>
              <p className="text-gray-600">Discover amazing people near you</p>
            </div>
            <div className="flex items-center gap-4">
              <button className="p-3 hover:bg-gray-100 rounded-xl transition-colors">
                <img
                  src="/chat/icons/search.png"
                  alt="Search"
                  className="w-6 h-6"
                />
              </button>
              <button className="p-3 hover:bg-gray-100 rounded-xl transition-colors">
                <img
                  src="/chat/icons/notification.png"
                  alt="Notifications"
                  className="w-6 h-6"
                />
              </button>
            </div>
          </div>

          {/* Active filters indicator */}
          <div className="mt-4 flex items-center gap-2">
            {filters.gender !== "Both" && (
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                {filters.gender}
              </span>
            )}
            {(filters.distanceMin !== 1 || filters.distanceMax !== 50) && (
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                {filters.distanceMin}-{filters.distanceMax}km
              </span>
            )}
            {filters.age !== 25 && (
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                Age: {filters.age}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      )}

      {/* Profile Grid */}
      <div
        className={`grid gap-4 ${
          isMobile
            ? "grid-cols-2 px-4"
            : "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3"
        }`}
      >
        {profiles &&
          profiles.length > 0 &&
          profiles.map(profile => (
            <ProfileCard
              key={profile.id}
              profile={profile}
              variant={isMobile ? "mobile" : "desktop"}
            />
          ))}
      </div>

      {/* Empty state */}
      {!isLoading &&
        profiles.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              No profiles found with current filters
            </p>
            <button
              onClick={() => {
                resetFilters();
                applyFilters(fetchFilteredProfiles);
              }}
              className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Reset Filters
            </button>
          </div>
        )}

      {/* Mobile Filter Modal */}
      {isMobile && (
        <MobileFilterModal
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={filters}
          onFiltersChange={updateFilters}
          onApplyFilters={handleApplyFilters}
        />
      )}
    </div>
  );

  // For mobile, return content directly (outside MainLayout)

  // For desktop, wrap in MainLayout with right sidebar
  return (
    <MainLayout rightSidebar={rightSidebar} rs={true}>
      {mainContent}
    </MainLayout>
  );
};

export default SayHello;
